
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function AdminPendingActions() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningReset, setIsRunningReset] = useState(false);
  
  const handleAddAdmin = async () => {
    if (!userId) {
      toast.error("Please enter a user ID");
      return;
    }

    setIsLoading(true);
    try {
      // Use the edge function instead of direct insert
      const { data, error } = await supabase.functions.invoke('add-admin-user', {
        body: { userId }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      toast.success("User added as admin successfully");
      setUserId("");
    } catch (error: any) {
      toast.error(`Failed to add admin: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const runResetFunction = async () => {
    setIsRunningReset(true);
    try {
      const { data, error } = await supabase.rpc('reset_custom_plan_resolution_counters');
      
      if (error) throw error;
      
      toast.success(`Successfully reset ${data} subscriptions`);
    } catch (error: any) {
      toast.error(`Failed to run reset: ${error.message}`);
    } finally {
      setIsRunningReset(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Add Admin User</CardTitle>
          <CardDescription>
            Give a user administrative privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input
                id="user-id"
                placeholder="Enter user ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleAddAdmin} 
              disabled={isLoading || !userId}
              className="w-full"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add as Admin
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Reset Custom Plan Counters</CardTitle>
          <CardDescription>
            Manually run the reset function for custom plan resolution counters
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This will reset company_resolutions_used and person_resolutions_used to 0 for custom plans that haven't been reset in the last 30 days.
            </p>
            <Button 
              onClick={runResetFunction} 
              disabled={isRunningReset}
              className="w-full"
            >
              {isRunningReset && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Run Reset Function
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
